import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrderDetails,
  updateOrderStatus,
  selectOrderDetails,
  clearOrderDetails,
} from "../../../slice/order/orderSlice";
import api from "../../../api/axios";
import {
  fetchRatingByOrder,
  selectOrderRating,
  clearOrderRating,
} from "../../../slice/rating/ratingSlice";

const BASE_URL = process.env.REACT_APP_API_URL;

/* ─── Status labels (matches orderSchema.status enum) ─────────── */
const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
  preparing: "Started Preparing",
  ready: "Ready",
  out_delivery: "Out for Delivery",
  delivered: "Delivered",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const money = (n = 0) => `$${Number(n || 0).toLocaleString("en-IN")}`;

/* ─── Simple presentational helpers ────────────────────────────── */
const Section = ({ title, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div style={{ padding: "12px 16px", borderBottom: "1px solid #eee" }}>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{title}</h6>
    </div>
    <div style={{ padding: 16 }}>{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid #f5f5f5",
      fontSize: 13,
    }}
  >
    <span style={{ color: "#777" }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#222", textAlign: "right" }}>
      {value || "—"}
    </span>
  </div>
);

const Badge = ({ text, color = "#374151", bg = "#f3f4f6" }) => (
  <span
    style={{
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 700,
      color,
      background: bg,
    }}
  >
    {text}
  </span>
);

const renderStars = (value = 0) => (
  <span style={{ fontSize: 16, letterSpacing: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} style={{ color: i < value ? "#f5a623" : "#e5e7eb" }}>
        ★
      </span>
    ))}
  </span>
);

/* ─── Main Component ───────────────────────────────────────────── */
const ViewOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const order = useSelector(selectOrderDetails);
  const loading = useSelector((s) => s.orders.loading);
  const updating = useSelector((s) => s.orders.updating);
  const error = useSelector((s) => s.orders.error);
  const orderRating = useSelector(selectOrderRating);

  const [newStatus, setNewStatus] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [trackingDirty, setTrackingDirty] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [trackingError, setTrackingError] = useState("");

  useEffect(() => {
    dispatch(fetchOrderDetails(id));
    dispatch(fetchRatingByOrder(id));
    return () => {
      dispatch(clearOrderDetails());
      dispatch(clearOrderRating());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (order) {
      setNewStatus(order.status || "pending");
      setTrackingUrl(order.trackingUrl || "");
      setTrackingDirty(false);
    }
  }, [order]);

  const handleSave = async () => {
    setTrackingError("");

    if (newStatus !== order.status) {
      await dispatch(updateOrderStatus({ orderId: order._id, orderStatus: newStatus }));
    }

    if (trackingDirty) {
      try {
        await api.patch(`/api/orders/${order._id}/tracking`, { trackingUrl });
        setTrackingDirty(false);
      } catch (err) {
        setTrackingError(err.response?.data?.message || "Failed to update tracking URL");
        return;
      }
    }

    setSavedMsg("Changes saved!");
    setTimeout(() => setSavedMsg(""), 2500);
    dispatch(fetchOrderDetails(id));
  };

  if (loading || !order) {
    return (
      <div className="container-fluid" style={{ paddingTop: 60, textAlign: "center", color: "#9ca3af" }}>
        {loading ? (
          <>
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            Loading order…
          </>
        ) : (
          <span style={{ color: "#ef4444" }}>{error || "Order not found."}</span>
        )}
      </div>
    );
  }

  const addr = order.shippingAddress || {};
  const isPaid = order.payment?.status === "paid";

  return (
    <div className="container-fluid" style={{ padding: 16 }}>
      {/* ── Top bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => navigate("/admin/order/recevied")}
            style={{
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>{order.orderId}</div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              Placed {formatDateTime(order.createdAt)}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Badge text={STATUS_LABELS[order.status] || order.status} />
          <Badge
            text={`Online - Success`}
            color={isPaid ? "#0f6e56" : "#854f0b"}
            bg={isPaid ? "#e1f5ee" : "#faeeda"}
          />
   
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 16 }}>
        {/* ════ LEFT COLUMN ════ */}
        <div>
          <Section title="Order Items">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #eee", color: "#777", textAlign: "left" }}>
                  <th style={{ padding: "6px 4px" }}>Item</th>
                  <th style={{ padding: "6px 4px", textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "6px 4px", textAlign: "right" }}>Price</th>
                  <th style={{ padding: "6px 4px", textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((item, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "8px 4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {item.images?.[0] && (
                          <img
                            src={`${BASE_URL}/uploads/products/${item.images[0]}`}
                            alt={item.name}
                            style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover" }}
                          />
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{item.name || "—"}</div>
                          {item.isCustom && (
                            <div style={{ fontSize: 11, color: "#92400e" }}>
                              🌸 Custom
                              {item.flowers ? ` · ${item.flowers}` : ""}
                              {item.kunjam_count > 0 ? ` · Kunjam: ${item.kunjam_count}` : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "8px 4px", textAlign: "center" }}>{item.quantity}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right" }}>{money(item.price)}</td>
                    <td style={{ padding: "8px 4px", textAlign: "right", fontWeight: 700 }}>
                      {money(item.totalPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<div style={{ marginTop: 16, maxWidth: 260, marginLeft: "auto" }}>
  <Row label="Subtotal" value={money(order.subtotal)} />
  <Row label="Minimum Order Fee" value={money(order.minimumOrderFee)} />
  <Row label="Discount" value={`− ${money(order.serviceCharge)}`} />
  <Row label="Shipping" value={money(order.shipping)} />
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      paddingTop: 8,
      fontWeight: 800,
      fontSize: 15,
    }}
  >
    <span>Total</span>
    <span>{money(order.totalAmount)}</span>
  </div>
</div>
          </Section>

          

          {order.comments && (
            <Section title="Comments">
              <div style={{ fontSize: 13, color: "#444" }}>{order.comments}</div>
            </Section>
          )}

          {(order.preferred_delivery_date || order.preferred_delivery_timeslot) && (
            <Section title="Preferred Delivery">
              <Row label="Date" value={order.preferred_delivery_date} />
              <Row label="Time Slot" value={order.preferred_delivery_timeslot} />
            </Section>
          )}
        </div>

        {/* ════ RIGHT COLUMN ════ */}
        <div>
          <Section title="Customer & Shipping">
            <Row label="Name" value={addr.name} />
            <Row label="Contact" value={addr.contactnumber} />
            <Row label="Location" value={addr.location} />
            <Row label="Block No." value={addr.block_number} />
            <Row label="Building" value={addr.building_name} />
            <Row label="Address Type" value={addr.address_type} />
            <Row label="Landmark" value={addr.landmark} />
          </Section>

          <Section title="Payment">
            <Row
              label="Method"
              value={
              
                   "Online"
                
              }
            />
            <Row label="Status" value="Success" />
            <Row label="Transaction ID" value={order.payment?.transactionId} />
          </Section>

          {order.trackingUrl && (
            <Section title="Tracking">
              
          <a      href={order.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 13, color: "#0284c7", wordBreak: "break-all" }}
              >
                {order.trackingUrl}
              </a>
            </Section>
          )}

          {order.estimatedDeliveryTime && (
            <Section title="Estimated Delivery">
              <Row label="Date" value={formatDate(order.estimatedDeliveryTime)} />
              <Row
                label="Time"
                value={new Date(order.estimatedDeliveryTime).toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              />
              {order.estimatedDuration && (
                <Row label="Duration" value={`${order.estimatedDuration} min`} />
              )}
            </Section>
          )}

          {orderRating && (
            <Section title="Customer Rating">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                {renderStars(orderRating.value)}
                <span style={{ fontWeight: 700, fontSize: 13 }}>{orderRating.value}/5</span>
              </div>
              {orderRating.review && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#555",
                    background: "#f9fafb",
                    padding: "8px 10px",
                    borderRadius: 8,
                  }}
                >
                  "{orderRating.review}"
                </div>
              )}
              <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 6 }}>
                Rated on {formatDate(orderRating.createdAt)}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewOrder;